import { useListContext } from "ra-core";
import { Link } from "react-router";
import { useIsMobile } from "@/hooks/use-mobile";
import { CreateButton } from "@/components/admin/create-button";
import { List } from "@/components/admin/list";
import { ListPagination } from "@/components/admin/list-pagination";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TopToolbar } from "../layout/TopToolbar";
import { LoadEmpty } from "./LoadEmpty";
import { LoadListFilter } from "./LoadListFilter";
import { LOAD_STATUS_CHOICES } from "./LoadInputs";

const LoadList = () => (
  <List
    resource="loads_summary"
    perPage={25}
    title={false}
    sort={{ field: "created_at", order: "DESC" }}
    actions={<LoadActions />}
    pagination={<ListPagination />}
  >
    <LoadListLayout />
  </List>
);

const LoadActions = () => (
  <TopToolbar>
    <CreateButton resource="loads" />
  </TopToolbar>
);

const LoadListLayout = () => {
  const { data, isPending, filterValues } = useListContext();
  const isMobile = useIsMobile();
  const hasFilters = filterValues && Object.keys(filterValues).length > 0;

  if (isPending) return null;
  if (!data?.length && !hasFilters) return <LoadEmpty />;

  return (
    <div className="w-full flex flex-col gap-4">
      {isMobile && <LoadListFilter />}
      <div className="w-full flex flex-row gap-8">
        {!isMobile && <LoadListFilter />}
        <div className="flex flex-col flex-1 min-w-0 gap-4">
          <Card className="py-0 overflow-hidden min-w-0">
            <LoadListContent />
          </Card>
        </div>
      </div>
    </div>
  );
};

const LoadListContent = () => {
  const { data, isPending } = useListContext();

  if (isPending) return null;
  if (!data || data.length === 0) return <LoadEmpty />;

  return (
    <div className="overflow-x-auto w-full">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b text-xs text-muted-foreground">
            <th className="text-left p-3 font-medium">Load #</th>
            <th className="text-left p-3 font-medium">Status</th>
            <th className="text-left p-3 font-medium">Pickup Date</th>
            <th className="text-left p-3 font-medium w-36">Vendor</th>
            <th className="text-left p-3 font-medium w-36">Customer</th>
            <th className="text-left p-3 font-medium">Main Commodity</th>
            <th className="text-left p-3 font-medium">Purchasing</th>
            <th className="text-left p-3 font-medium">Sales</th>
            <th className="text-left p-3 font-medium">Logistics</th>
          </tr>
        </thead>
        <tbody>
          {data.map((load: any) => {
            const statusLabel =
              LOAD_STATUS_CHOICES.find((s) => s.id === load.status)?.name ??
              load.status;
            return (
              <tr key={load.id} className="border-b hover:bg-muted/50">
                <td className="p-3 whitespace-nowrap">
                  <Link
                    to={`/loads/${load.id}/show`}
                    className="font-medium text-primary hover:underline"
                  >
                    {load.load_number || "—"}
                  </Link>
                </td>
                <td className="p-3 whitespace-nowrap">
                  {statusLabel ? (
                    <Badge variant="outline">{statusLabel}</Badge>
                  ) : (
                    "—"
                  )}
                </td>
                <td className="p-3 whitespace-nowrap">
                  {load.pickup_date
                    ? new Date(load.pickup_date).toLocaleDateString()
                    : "—"}
                </td>
                <td className="p-3 max-w-36 truncate" title={load.vendor_company_name || undefined}>
                  {load.vendor_company_name || "—"}
                </td>
                <td className="p-3 max-w-36 truncate" title={load.customer_company_name || undefined}>
                  {load.customer_company_name || "—"}
                </td>
                <td className="p-3">{load.main_commodity || "—"}</td>
                <td className="p-3 whitespace-nowrap">
                  {load.purchasing_owner_name || "—"}
                </td>
                <td className="p-3 whitespace-nowrap">
                  {load.sales_owner_name || "—"}
                </td>
                <td className="p-3 whitespace-nowrap">
                  {load.logistics_owner_name || "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export { LoadList };
export default LoadList;
